import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useDiscover } from '../../hooks/useDiscover';
import { useNavigation } from '../../hooks/useNavigation';
import BackgroundShapes from '../common/BackgroundShapes';
import DiscoverUserCard from './DiscoverUserCard';
import DiscoverFilters from './DiscoverFilters';
import { discoverAPI } from '../../services/discoverService';
import { DiscoverUser, DiscoverFilters as DiscoverFiltersType } from '../../types/discover';

interface DiscoverPageProps {
  onNavigate: (page: string) => void;
}

const DiscoverPage: React.FC<DiscoverPageProps> = ({ onNavigate }) => {
  const { user, isAuthenticated } = useAuth();
  const navigation = useNavigation();
  const {
    users,
    suggestions,
    loading,
    error,
    filters,
    pagination,
    loadUsers,
    loadMoreUsers,
    loadSuggestions,
    applyFilters,
    clearFilters,
    goToPage,
    goToNextPage,
    goToPrevPage,
    setItemsPerPage
  } = useDiscover();

  const [activeTab, setActiveTab] = useState<'discover' | 'suggestions'>('discover');
  const [showFilters, setShowFilters] = useState(false);
  const [requireNeighborhoodOverlap, setRequireNeighborhoodOverlap] = useState(false);

  // Guest-only state — used when isAuthenticated is false
  const [guestUsers, setGuestUsers] = useState<DiscoverUser[]>([]);
  const [guestLoading, setGuestLoading] = useState(false);
  const [guestFilters, setGuestFilters] = useState<DiscoverFiltersType>({});
  const [guestPagination, setGuestPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0,
    hasNext: false,
    hasPrev: false
  });

  // Load initial data for authenticated users
  useEffect(() => {
    if (user) {
      loadUsers(user);
      loadSuggestions(user);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]); // Only load once when user is available

  // Guest fetch helper
  const fetchGuestUsers = useCallback(async (filters: DiscoverFiltersType, page: number) => {
    setGuestLoading(true);
    try {
      const result = await discoverAPI.browsePublicUsers(filters, page, 20);
      if (result.success && result.data) {
        setGuestUsers(result.data);
        setGuestPagination({
          page: result.pagination?.page ?? 1,
          totalPages: result.pagination?.totalPages ?? 1,
          total: result.pagination?.total ?? 0,
          hasNext: result.pagination?.hasMore ?? false,
          hasPrev: result.pagination?.hasPrev ?? false
        });
      }
    } catch (err) {
      console.error('Guest browse error:', err);
    } finally {
      setGuestLoading(false);
    }
  }, []);

  // Load initial data for guests
  useEffect(() => {
    if (!isAuthenticated) {
      fetchGuestUsers({}, 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const handleApplyFilters = (newFilters: any) => {
    if (!isAuthenticated) {
      setGuestFilters(newFilters);
      fetchGuestUsers(newFilters, 1);
      return;
    }
    applyFilters(newFilters);
    if (user) {
      loadUsers(user, newFilters, 1, true);
    }
  };

  const handleNeighborhoodToggle = (checked: boolean) => {
    setRequireNeighborhoodOverlap(checked);
    // Local state only; applied when user clicks Apply Filter
  };

  const handleClearFilters = () => {
    if (!isAuthenticated) {
      setGuestFilters({});
      fetchGuestUsers({}, 1);
      return;
    }
    setRequireNeighborhoodOverlap(false);
    clearFilters();
    if (user) {
      loadUsers(user, {}, 1, true);
    }
  };

  const handlePageChange = (page: number) => {
    if (user) {
      goToPage(user, page);
    }
  };

  const handleItemsPerPageChange = (limit: number) => {
    if (user) {
      setItemsPerPage(user, limit);
    }
  };

  const handleLoadMore = () => {
    if (user && pagination.hasMore) {
      loadMoreUsers(user);
    }
  };

  const handleBackToDashboard = () => {
    try {
      navigation.goToDashboard();
    } catch {
      onNavigate('dashboard');
    }
  };

  // Guest discover tab — no Suggestions, own pagination
  const renderGuestContent = () => (
    <div className="space-y-6">
      {/* Filters */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-coffee">Discover Roommates</h2>
          <div className="flex space-x-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="bg-white border-2 border-coffee text-coffee px-4 py-2 rounded-lg hover:bg-cream transition-colors"
            >
              {showFilters ? 'Hide' : 'Show'} Filters
            </button>
            <button
              onClick={handleClearFilters}
              className="bg-white border-2 border-terracotta text-terracotta px-4 py-2 rounded-lg hover:bg-terracotta hover:text-white transition-colors"
            >
              Clear All
            </button>
          </div>
        </div>

        {showFilters && (
          <DiscoverFilters
            onApplyFilters={handleApplyFilters}
            currentFilters={guestFilters}
            onClearFilters={handleClearFilters}
          />
        )}
      </div>

      {guestLoading && (
        <div className="text-center py-12 text-coffee">Loading users...</div>
      )}

      {!guestLoading && guestUsers.length > 0 ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {guestUsers.map((u) => (
              <DiscoverUserCard
                key={u.id}
                user={u}
                onNavigate={onNavigate}
                isAuthenticated={false}
              />
            ))}
          </div>

          {/* Guest pagination info */}
          {guestPagination.total > 0 && (
            <div className="text-center text-coffee opacity-80 text-sm mb-4">
              Page {guestPagination.page} of {guestPagination.totalPages} ({guestPagination.total} users)
            </div>
          )}

          {/* Guest pagination controls */}
          {guestPagination.totalPages > 1 && (
            <div className="flex justify-center items-center space-x-2 mt-6">
              <button
                onClick={() => fetchGuestUsers(guestFilters, guestPagination.page - 1)}
                disabled={guestLoading || !guestPagination.hasPrev}
                className="bg-white border-2 border-coffee text-coffee px-4 py-2 rounded-lg hover:bg-cream transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ← Previous
              </button>
              <button
                onClick={() => fetchGuestUsers(guestFilters, guestPagination.page + 1)}
                disabled={guestLoading || !guestPagination.hasNext}
                className="bg-white border-2 border-coffee text-coffee px-4 py-2 rounded-lg hover:bg-cream transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next →
              </button>
            </div>
          )}
        </>
      ) : !guestLoading ? (
        <div className="text-center py-12">
          <div className="text-coffee text-xl mb-4">No users found</div>
          <div className="text-coffee opacity-80 mb-6">
            Try adjusting your filters or check back later for new users
          </div>
          <button
            onClick={handleClearFilters}
            className="bg-white border-2 border-coffee text-coffee px-6 py-2 rounded-lg hover:bg-cream transition-colors"
          >
            Clear Filters
          </button>
        </div>
      ) : null}
    </div>
  );

  const renderContent = () => {
    // Guests see only the discover grid — no Suggestions tab
    if (!isAuthenticated) {
      return renderGuestContent();
    }

    switch (activeTab) {
      case 'suggestions':
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-coffee mb-2">People You May Like</h2>
              <p className="text-coffee opacity-80">High compatibility matches based on your preferences</p>
            </div>
            {suggestions.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {suggestions.map((user) => (
                  <DiscoverUserCard
                    key={user.id}
                    user={user}
                    onNavigate={onNavigate}
                    isSuggestion={true}
                    isAuthenticated={true}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-coffee text-xl mb-4">No suggestions available</div>
                <div className="text-coffee opacity-80">Complete your profile to get better suggestions</div>
              </div>
            )}
          </div>
        );

      default: // discover
        return (
          <div className="space-y-6">
            {/* Filters */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-coffee">Discover Roommates</h2>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="bg-white border-2 border-coffee text-coffee px-4 py-2 rounded-lg hover:bg-cream transition-colors"
                  >
                    {showFilters ? 'Hide' : 'Show'} Filters
                  </button>
                  <button
                    onClick={handleClearFilters}
                    className="bg-white border-2 border-terracotta text-terracotta px-4 py-2 rounded-lg hover:bg-terracotta hover:text-white transition-colors"
                  >
                    Clear All
                  </button>
                </div>
              </div>

              {showFilters && (
                <DiscoverFilters
                  onApplyFilters={handleApplyFilters}
                  currentFilters={filters}
                  onClearFilters={handleClearFilters}
                  requireNeighborhoodOverlap={requireNeighborhoodOverlap}
                  onNeighborhoodOverlapChange={handleNeighborhoodToggle}
                />
              )}
            </div>

            {/* Error State */}
            {error && (
              <div className="bg-red-100 border-2 border-red-500 rounded-xl p-4 mb-6">
                <div className="text-red-700">{error}</div>
                <button
                  onClick={() => user && loadUsers(user)}
                  className="text-red-600 hover:text-red-800 text-sm mt-2 underline"
                >
                  Try Again
                </button>
              </div>
            )}

            {/* Users Grid */}
            {users.length > 0 ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {users.map((user) => (
                    <DiscoverUserCard
                      key={user.id}
                      user={user}
                      onNavigate={onNavigate}
                      isAuthenticated={true}
                    />
                  ))}
                </div>

                {/* Pagination Info */}
                {pagination.total > 0 && (
                  <div className="text-center text-coffee opacity-80 text-sm mb-4">
                    Showing {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} users
                    {pagination.totalPages > 1 && ` (Page ${pagination.page} of ${pagination.totalPages})`}
                  </div>
                )}

                {/* Authenticated pagination controls — only render when user is not null (F7) */}
                {isAuthenticated && pagination.totalPages > 1 && (
                  <div className="flex flex-col items-center space-y-4 mt-6">
                    {/* Per-page selector */}
                    <div className="flex items-center space-x-2">
                      <label className="text-coffee opacity-80 text-sm">Show:</label>
                      <select
                        value={pagination.limit}
                        onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value, 10))}
                        disabled={loading}
                        className="bg-white text-coffee border border-coffee/30 rounded-lg px-3 py-1 focus:outline-none focus:border-terracotta focus:ring-1 focus:ring-terracotta disabled:opacity-50"
                      >
                        <option value={10} className="text-coffee bg-white">10</option>
                        <option value={20} className="text-coffee bg-white">20</option>
                        <option value={50} className="text-coffee bg-white">50</option>
                        <option value={200} className="text-coffee bg-white">200</option>
                      </select>
                      <span className="text-coffee opacity-80 text-sm">per page</span>
                    </div>

                    {/* Page navigation */}
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handlePageChange(1)}
                        disabled={loading || pagination.page === 1}
                        className="bg-white border-2 border-coffee text-coffee px-3 py-2 rounded-lg hover:bg-cream transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        First
                      </button>
                      <button
                        onClick={() => goToPrevPage(user!)}
                        disabled={loading || !pagination.hasPrev}
                        className="bg-white border-2 border-coffee text-coffee px-4 py-2 rounded-lg hover:bg-cream transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        ← Previous
                      </button>

                      {/* Page numbers */}
                      <div className="flex items-center space-x-1">
                        {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                          let pageNum: number;
                          if (pagination.totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (pagination.page <= 3) {
                            pageNum = i + 1;
                          } else if (pagination.page >= pagination.totalPages - 2) {
                            pageNum = pagination.totalPages - 4 + i;
                          } else {
                            pageNum = pagination.page - 2 + i;
                          }

                          return (
                            <button
                              key={pageNum}
                              onClick={() => handlePageChange(pageNum)}
                              disabled={loading}
                              className={`px-3 py-2 rounded-lg transition-colors ${
                                pagination.page === pageNum
                                  ? 'bg-terracotta text-white border-2 border-coffee'
                                  : 'bg-white border-2 border-coffee text-coffee hover:bg-cream'
                              } disabled:opacity-50`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>

                      <button
                        onClick={() => goToNextPage(user!)}
                        disabled={loading || !pagination.hasMore}
                        className="bg-white border-2 border-coffee text-coffee px-4 py-2 rounded-lg hover:bg-cream transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next →
                      </button>
                      <button
                        onClick={() => handlePageChange(pagination.totalPages)}
                        disabled={loading || pagination.page === pagination.totalPages}
                        className="bg-white border-2 border-coffee text-coffee px-3 py-2 rounded-lg hover:bg-cream transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Last
                      </button>
                    </div>
                  </div>
                )}

                {/* Load More Button (fallback for infinite scroll style) */}
                {pagination.hasMore && pagination.totalPages <= 1 && (
                  <div className="text-center mt-8">
                    <button
                      onClick={handleLoadMore}
                      disabled={loading}
                      className="bg-white border-2 border-coffee text-coffee px-6 py-3 rounded-lg hover:bg-cream transition-colors disabled:opacity-50"
                    >
                      {loading ? 'Loading...' : 'Load More Users'}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <div className="text-coffee text-xl mb-4">No users found</div>
                <div className="text-coffee opacity-80 mb-6">
                  Try adjusting your filters or check back later for new users
                </div>
                <button
                  onClick={handleClearFilters}
                  className="bg-white border-2 border-coffee text-coffee px-6 py-2 rounded-lg hover:bg-cream transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        );
    }
  };

  if (isAuthenticated && loading && users.length === 0) {
    return (
      <div className="min-h-screen bg-paper relative">
        <BackgroundShapes />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-coffee text-xl">Discovering users...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper relative">
      <BackgroundShapes />

      <div className="container mx-auto px-4 py-8 relative z-10">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-coffee mb-2">
              Discover Roommates
            </h1>
            <p className="text-coffee opacity-80">
              Find your perfect roommate match
            </p>
          </div>
          {isAuthenticated && (
            <button
              onClick={handleBackToDashboard}
              className="bg-white border-2 border-coffee text-coffee px-4 py-2 rounded-lg hover:bg-cream transition-colors"
            >
              ← Back to Dashboard
            </button>
          )}
        </div>

        {/* Tabs — only shown for authenticated users (F6: hide Suggestions tab for guests) */}
        {isAuthenticated && (
          <div className="flex w-full mb-8">
            <button
              onClick={() => setActiveTab('discover')}
              className={`flex-1 px-6 py-3 rounded-t-lg transition-colors border-2 ${
                activeTab === 'discover'
                  ? 'bg-cream border-coffee text-coffee font-semibold'
                  : 'bg-white border-coffee border-opacity-30 text-coffee opacity-70 hover:opacity-100'
              }`}
            >
              Discover
            </button>
            <button
              onClick={() => setActiveTab('suggestions')}
              className={`flex-1 px-6 py-3 rounded-t-lg transition-colors border-2 ${
                activeTab === 'suggestions'
                  ? 'bg-cream border-coffee text-coffee font-semibold'
                  : 'bg-white border-coffee border-opacity-30 text-coffee opacity-70 hover:opacity-100'
              }`}
            >
              Suggestions
            </button>
          </div>
        )}

        {/* Content */}
        {renderContent()}
      </div>
    </div>
  );
};

export default DiscoverPage;
